// M09 - EducaFileCard
//
// Card individual de fichero. Vista visual:
//  - Preview de imagen via stream si es publica (kind=image+visibility=public)
//  - Icono grande por kind para el resto
//  - Badge de visibilidad (public/private)
//  - Menu de acciones (copiar URL, editar, borrar) en una dropdown
//
// Las imagenes privadas muestran icono (no preview) porque el <img>
// del browser no puede enviar Authorization header sin trabajo extra.

import React from "react";
import { Dropdown, Tag, Tooltip, message } from "antd";
import {
  FileImageOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  FileOutlined,
  GlobalOutlined,
  LockOutlined,
  MoreOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import styles from "./EducaFileCard.module.scss";

const KIND_ICON = {
  image: FileImageOutlined,
  video: VideoCameraOutlined,
  audio: AudioOutlined,
  doc: FilePdfOutlined,
  archive: FileZipOutlined,
};

const KIND_LABEL = {
  image: "Imagen",
  video: "Video",
  audio: "Audio",
  doc: "Documento",
  archive: "Comprimido",
};

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function EducaFileCard({
  file,
  streamUrl,
  onCopyUrl,
  onEdit,
  onDelete,
  onPreview,
  onDownload,
}) {
  const Icon = KIND_ICON[file.kind] || FileOutlined;
  const isPublicImage = file.kind === "image" && file.visibility === "public";

  const items = [
    {
      key: "copy",
      icon: <CopyOutlined />,
      label: "Copiar URL",
      onClick: () => {
        try {
          navigator.clipboard.writeText(streamUrl);
          message.success("URL copiada");
        } catch (e) {
          message.error("No se pudo copiar al portapapeles");
        }
        onCopyUrl?.(file);
      },
    },
    {
      key: "preview",
      icon: <EyeOutlined />,
      label: "Abrir",
      onClick: () => onPreview?.(file),
    },
    {
      key: "download",
      icon: <DownloadOutlined />,
      label: "Descargar",
      onClick: () => onDownload?.(file),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Editar",
      onClick: () => onEdit?.(file),
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Borrar",
      danger: true,
      onClick: () => onDelete?.(file),
    },
  ];

  return (
    <div className={styles.card}>
      <div className={styles.cover}>
        {isPublicImage ? (
          <img
            src={streamUrl}
            alt={file.name}
            className={styles.coverImg}
            loading="lazy"
          />
        ) : (
          <Icon className={styles.coverIcon} />
        )}

        <div className={styles.badges}>
          {file.visibility === "public" ? (
            <Tooltip title="URL accesible sin autenticacion">
              <Tag color="green" icon={<GlobalOutlined />}>
                Publica
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip title="Requiere JWT admin para acceder">
              <Tag color="default" icon={<LockOutlined />}>
                Privada
              </Tag>
            </Tooltip>
          )}
        </div>

        <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
          <button
            type="button"
            className={styles.menuBtn}
            aria-label="Acciones"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      <div className={styles.body}>
        <div className={styles.name} title={file.name}>
          {file.name}
        </div>
        <div className={styles.meta}>
          <span className={styles.kind}>
            {KIND_LABEL[file.kind] || file.kind}
          </span>
          <span className={styles.dot}>·</span>
          <span>{humanSize(file.size)}</span>
        </div>
      </div>
    </div>
  );
}
